import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import { cardClass, primaryText, largeTextSize, semiBoldText } from '../../styling/stylingHelper';
import { GraphCard } from '../ui/GraphCard';
import { NoDataInPeriod } from '../ui/NoDataInPeriod';
import { RestaurantFilterDropdown } from '../ui/RestaurantFilterDropdown';
import { getRestaurantColor } from '../../utils/restaurantColors';
import { calculateRevenueGraphData } from '../../utils/revenueHistoryCalculations';
import { RevenueHistory } from '../../services/revenuehistory/revenueHistoryService';
import { FrontendRestaurant } from '../../services/restaurant/restaurantService';
import { View } from '../orderhistory/types';

interface RevenueTrendMultiProps {
  /**
   * Revenue grouped by restaurantId, NOT scoped by the restaurant filter — every restaurant
   * with data is present so the multi-select can always offer all of them. Also NOT scoped
   * to the selected period: the plotted window reaches back 11 periods before it, so this
   * carries the full (progressively loaded) history and the window scoping happens here.
   */
  revenueByRestaurant: Record<string, RevenueHistory[]>;
  restaurants: FrontendRestaurant[];
  startDate: Date;
  endDate: Date;
  view: View;
  showExVat: boolean;
  /**
   * The page-level restaurant filter (shared with the filter modal). Empty = all restaurants
   * selected. Toggling a restaurant here filters the entire page's data.
   */
  selectedRestaurantIds: string[];
  setSelectedRestaurantIds: (ids: string[]) => void;
}

/**
 * Multi-restaurant revenue trend: one area/line series per restaurant, with a multi-select
 * to choose which restaurants to plot. Reuses the shared `GraphCard` (multi-series) and the
 * existing `calculateRevenueGraphData` helper (one call per restaurant over the same period).
 */
export const RevenueTrendMulti: React.FC<RevenueTrendMultiProps> = ({
  revenueByRestaurant,
  restaurants,
  startDate,
  endDate,
  view,
  showExVat,
  selectedRestaurantIds,
  setSelectedRestaurantIds,
}) => {
  const { t } = useTranslation();
  const nameMap = useMemo(() => new Map(restaurants.map(r => [r.id, r.name])), [restaurants]);

  // Per-restaurant values over the PLOTTED window (not just the selected period), computed
  // once and reused for ordering, the dropdown, the legend and the series. Restaurants with
  // nothing in the window are dropped so the full history behind `revenueByRestaurant`
  // doesn't leak every restaurant that ever had revenue into the selector.
  const windowedRestaurants = useMemo(() => {
    return Object.keys(revenueByRestaurant)
      .filter(id => id !== 'unknown')
      .map(id => {
        const points = calculateRevenueGraphData(revenueByRestaurant[id] || [], startDate, endDate, view, showExVat);
        const values = points.map(d => d.value);
        return {
          id,
          name: nameMap.get(id) || id,
          labels: points.map(d => d.label),
          values,
          total: values.reduce((sum, v) => sum + v, 0),
        };
      })
      .filter(r => r.values.some(v => v !== 0))
      .sort((a, b) => b.total - a.total);
  }, [revenueByRestaurant, nameMap, startDate, endDate, view, showExVat]);

  const allIds = useMemo(() => windowedRestaurants.map(r => r.id), [windowedRestaurants]);
  const byId = useMemo(() => new Map(windowedRestaurants.map(r => [r.id, r])), [windowedRestaurants]);

  // Selection mirrors the page-level restaurant filter: empty = ALL restaurants selected.
  const effectiveSelected = selectedRestaurantIds.length > 0 ? selectedRestaurantIds : allIds;

  // Shared x-axis: every restaurant's series spans the same buckets, so reuse the labels
  // already computed above instead of re-walking the whole history (stable regardless of
  // selection). Only empty when there's nothing to plot — the empty state renders then.
  const xLabels = windowedRestaurants[0]?.labels ?? [];

  const series = useMemo(() => {
    const out: { label: string; values: number[]; color: string }[] = [];
    for (const id of effectiveSelected) {
      const r = byId.get(id);
      if (r) out.push({ label: r.name, values: r.values, color: getRestaurantColor(id) });
    }
    return out;
  }, [effectiveSelected, byId]);

  // Visible restaurants → coloured-swatch legend (mirrors the new UI design).
  const legendItems = useMemo(() => {
    const out: { id: string; name: string; color: string }[] = [];
    for (const id of effectiveSelected) {
      const r = byId.get(id);
      if (r) out.push({ id, name: r.name, color: getRestaurantColor(id) });
    }
    return out;
  }, [effectiveSelected, byId]);

  // Selector + legend, rendered inside the card header (top-right).
  const headerRight = (
    <div className="flex flex-col items-end gap-2.5">
      <RestaurantFilterDropdown
        restaurants={windowedRestaurants.map(r => ({ id: r.id, name: r.name }))}
        selectedRestaurantIds={selectedRestaurantIds}
        setSelectedRestaurantIds={setSelectedRestaurantIds}
      />
      {legendItems.length > 0 && (
        <div className="flex items-center gap-3 flex-wrap justify-end">
          {legendItems.map(item => (
            <div key={item.id} className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-[3px]" style={{ backgroundColor: item.color }} />
              <span className="text-sm font-normal text-textPrimary dark:text-textPrimary-dark">
                {item.name}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Empty state (no restaurants selected, or none with data): keep the card header with the
  // restaurant dropdown so the user can always re-select — only the plot area shows "no data".
  return series.length === 0 ? (
    <div className={cn('w-full', cardClass)}>
      <div className="flex justify-between items-start gap-4 p-4">
        <h3 className={cn(primaryText, largeTextSize, semiBoldText)}>
          {t('revenueHistory.revenueTrendByRestaurant')}
        </h3>
        {headerRight}
      </div>
      <div className="flex items-center justify-center" style={{ minHeight: 280 }}>
        <NoDataInPeriod />
      </div>
    </div>
  ) : (
    <GraphCard
      title={t('revenueHistory.revenueTrendByRestaurant')}
      xLabels={xLabels}
      series={series}
      height={330}
      hideLegend
      headerRight={headerRight}
    />
  );
};

export default RevenueTrendMulti;
